import type { Witness } from '../store/booksStore'
import type { AnchoredRow, AnchoredRowMetadata } from '../types/anchoredRowTypes'
import { resolveLedgerLabelPrefix } from './contextLabelPrefix'

type ChapterImportPayload = {
  chapter?: { id?: string; label_prefix?: string; name?: string; title?: string }
  witnesses?: Array<Record<string, any>>
  segmentation_results?: Record<string, any>
  anchored_rows?: Array<Record<string, any>>
}

type StagedBuildArtifact = {
  diagnostics?: Record<string, any>
  chapter_import_payload: ChapterImportPayload
}

interface ApplyWorkbenchBuildArtifactOptions {
  contextId: string
  existingWitnesses: Witness[]
  artifact: StagedBuildArtifact
  loadChapterRows: (chapterId: string) => Promise<AnchoredRow[]>
  getRowsForChapter: (chapterId: string) => AnchoredRow[]
  upsertRows: (chapterId: string, rows: AnchoredRow[], options?: { autoRelabel?: boolean }) => Promise<void>
  replaceResolvedRows: (
    chapterId: string,
    previousRows: AnchoredRow[],
    nextRows: AnchoredRow[],
    options?: { autoRelabel?: boolean }
  ) => Promise<void>
  updateWitnessProcessingResultsBatch: (updates: Array<{
    witnessId: string
    type: 'segmentation'
    result: any
    status?: Witness['processing_status']
  }>) => Promise<void>
  createWitness: (chapterId: string, witnessData: Omit<Witness, 'id' | 'chapter_id' | 'book_id' | 'created_at' | 'processing_status'>) => Promise<string>
  ensureWitnessSourceObjectLink?: (witnessId: string, sourceData?: Partial<Pick<Witness, 'sigla' | 'description' | 'manuscript_date' | 'library' | 'city' | 'shelfmark' | 'witness_type'>>) => Promise<string | null>
  replaceExistingRows?: boolean
}

const normalizeSiglaList = (value: unknown): string[] => (
  Array.isArray(value)
    ? value
      .map(item => String(item || '').trim())
      .filter(Boolean)
    : []
)

const normalizeAnchoredRowMetadata = (value: unknown): AnchoredRowMetadata | undefined => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined
  }
  return { ...(value as Record<string, unknown>) } as AnchoredRowMetadata
}

const getRepresentativeSiglaFromArtifact = (artifact: StagedBuildArtifact): Set<string> | null => {
  const diagnostics = artifact.diagnostics || {}
  const diagnosticSigla = normalizeSiglaList(diagnostics.representative_witness_sigla)
  const diagnosticPlan = diagnostics.adaptive_build_plan
  const planSigla = typeof diagnosticPlan === 'object' && diagnosticPlan !== null
    ? normalizeSiglaList((diagnosticPlan as Record<string, unknown>).representative_witness_sigla)
    : []
  const representativeSigla = diagnosticSigla.length > 0 ? diagnosticSigla : planSigla
  return representativeSigla.length > 0 ? new Set(representativeSigla) : null
}

interface ApplyWorkbenchBuildArtifactWitnessReplayOptions {
  contextId: string
  existingWitnesses: Witness[]
  currentRows: AnchoredRow[]
  artifact: StagedBuildArtifact
  upsertRows: (chapterId: string, rows: AnchoredRow[], options?: { autoRelabel?: boolean }) => Promise<void>
  updateWitnessProcessingResultsBatch: (updates: Array<{
    witnessId: string
    type: 'segmentation'
    result: any
    status?: Witness['processing_status']
  }>) => Promise<void>
}

export const applyWorkbenchBuildArtifactToContext = async ({
  contextId,
  existingWitnesses,
  artifact,
  loadChapterRows,
  getRowsForChapter,
  upsertRows,
  replaceResolvedRows,
  updateWitnessProcessingResultsBatch,
  createWitness,
  ensureWitnessSourceObjectLink,
  replaceExistingRows = false,
}: ApplyWorkbenchBuildArtifactOptions): Promise<{ rowCount: number; witnessCount: number }> => {
  await loadChapterRows(contextId)
  const existingRows = getRowsForChapter(contextId)
  if (existingRows.length > 0 && !replaceExistingRows) {
    throw new Error('Import into non-empty contexts is not supported yet.')
  }

  const payload = artifact.chapter_import_payload || {}
  const artifactWitnesses = Array.isArray(payload.witnesses) ? payload.witnesses : []
  const chapterLabelPrefix = resolveLedgerLabelPrefix({
    contextLabelPrefix: typeof payload.chapter?.label_prefix === 'string' ? payload.chapter.label_prefix : undefined,
    contextTitle: typeof payload.chapter?.name === 'string' ? payload.chapter.name : payload.chapter?.title,
    witnessSigla: artifactWitnesses.map(witness => typeof witness?.sigla === 'string' ? witness.sigla : ''),
    fallbackPrefix: 'Cx'
  })
  const segmentationResults = payload.segmentation_results || {}
  const witnessIdsBySigla = new Map(existingWitnesses.map(witness => [witness.sigla, witness.id]))
  const representativeSigla = getRepresentativeSiglaFromArtifact(artifact)

  const importedWitnessIdsByArtifactId = new Map<string, string>()
  const representativeArtifactWitnessIds = new Set<string>()

  for (const artifactWitness of artifactWitnesses) {
    const sigla = typeof artifactWitness?.sigla === 'string' ? artifactWitness.sigla.trim() : ''
    if (!sigla) {
      continue
    }
    const isRepresentativeWitness = !representativeSigla || representativeSigla.has(sigla)

    let witnessId = witnessIdsBySigla.get(sigla)
    if (!witnessId) {
      witnessId = await createWitness(contextId, {
        sigla,
        description: typeof artifactWitness?.description === 'string' ? artifactWitness.description : sigla,
        manuscript_date: typeof artifactWitness?.manuscript_date === 'string' ? artifactWitness.manuscript_date : undefined,
        library: typeof artifactWitness?.library === 'string' ? artifactWitness.library : '',
        city: typeof artifactWitness?.city === 'string' ? artifactWitness.city : undefined,
        shelfmark: typeof artifactWitness?.shelfmark === 'string' ? artifactWitness.shelfmark : undefined,
        witness_type: artifactWitness?.witness_type,
        transcription_type: artifactWitness?.transcription_type,
        coverage_status: artifactWitness?.coverage_status,
        lacunose_status: artifactWitness?.lacunose_status,
        uploaded_file: artifactWitness?.uploaded_file,
        preprocessing_result: artifactWitness?.preprocessing_result,
        segmentation_result: isRepresentativeWitness ? artifactWitness?.segmentation_result : undefined,
        segmentation_runs: isRepresentativeWitness ? artifactWitness?.segmentation_runs : undefined,
        active_segmentation_run: isRepresentativeWitness ? artifactWitness?.active_segmentation_run : undefined,
        last_modified: Date.now(),
        version: 1
      })
      witnessIdsBySigla.set(sigla, witnessId)
    }

    if (ensureWitnessSourceObjectLink) {
      await ensureWitnessSourceObjectLink(witnessId, {
        sigla,
        description: typeof artifactWitness?.description === 'string' ? artifactWitness.description : sigla,
        manuscript_date: typeof artifactWitness?.manuscript_date === 'string' ? artifactWitness.manuscript_date : undefined,
        library: typeof artifactWitness?.library === 'string' ? artifactWitness.library : '',
        city: typeof artifactWitness?.city === 'string' ? artifactWitness.city : undefined,
        shelfmark: typeof artifactWitness?.shelfmark === 'string' ? artifactWitness.shelfmark : undefined,
        witness_type: artifactWitness?.witness_type
      })
    }

    const artifactWitnessId = typeof artifactWitness?.id === 'string' ? artifactWitness.id : sigla
    importedWitnessIdsByArtifactId.set(artifactWitnessId, witnessId)
    if (isRepresentativeWitness) {
      representativeArtifactWitnessIds.add(artifactWitnessId)
    }
  }

  const segmentationUpdates = artifactWitnesses
    .map(artifactWitness => {
      const artifactWitnessId = typeof artifactWitness?.id === 'string' ? artifactWitness.id : ''
      const mappedWitnessId = importedWitnessIdsByArtifactId.get(artifactWitnessId)
      const result = segmentationResults[artifactWitnessId] || artifactWitness?.segmentation_result
      if (
        !mappedWitnessId
        || !result
        || (representativeSigla && !representativeArtifactWitnessIds.has(artifactWitnessId))
      ) {
        return null
      }
      return {
        witnessId: mappedWitnessId,
        type: 'segmentation' as const,
        result,
        status: 'completed' as const
      }
    })
    .filter((entry): entry is NonNullable<typeof entry> => Boolean(entry))

  if (segmentationUpdates.length > 0) {
    await updateWitnessProcessingResultsBatch(segmentationUpdates)
  }

  const nowIso = new Date().toISOString()
  const importedRows = (Array.isArray(payload.anchored_rows) ? payload.anchored_rows : [])
    .map((row, index): AnchoredRow | null => {
      const rawMappings = Array.isArray(row?.unitMappings) ? row.unitMappings : []
      const unitMappings = rawMappings
        .map((mapping: any) => {
          const artifactWitnessId = typeof mapping?.witnessId === 'string' ? mapping.witnessId : ''
          if (representativeSigla && !representativeArtifactWitnessIds.has(artifactWitnessId)) {
            return null
          }
          const remappedWitnessId = importedWitnessIdsByArtifactId.get(artifactWitnessId)
          const unitUuid = typeof mapping?.unitUuid === 'string' ? mapping.unitUuid : ''
          if (!remappedWitnessId || !unitUuid) {
            return null
          }
          return {
            witnessId: remappedWitnessId,
            unitUuid,
            unitLabel: typeof mapping?.unitLabel === 'string' ? mapping.unitLabel : undefined,
            textSnapshot: typeof mapping?.textSnapshot === 'string' ? mapping.textSnapshot : undefined,
            confidence: typeof mapping?.confidence === 'number' ? mapping.confidence : undefined,
            excluded: typeof mapping?.excluded === 'boolean' ? mapping.excluded : undefined,
            addedAt: typeof mapping?.addedAt === 'string' ? mapping.addedAt : nowIso
          }
        })
        .filter((mapping): mapping is NonNullable<typeof mapping> => Boolean(mapping))

      if (unitMappings.length === 0) {
        return null
      }

      const rawStatus = typeof row?.status === 'string' ? row.status : 'auto'
      const status: AnchoredRow['status'] = rawStatus === 'confirmed' || rawStatus === 'needs-review'
        ? rawStatus
        : 'auto'

      return {
        id: typeof row?.id === 'string' && row.id.trim().length > 0
          ? `build_import::${contextId}::${row.id}`
          : `build_import::${contextId}::${index + 1}`,
        chapterId: contextId,
        labelPrefix: resolveLedgerLabelPrefix({
          contextLabelPrefix: chapterLabelPrefix,
          secondaryPrefix: typeof row?.labelPrefix === 'string' ? row.labelPrefix : undefined,
          witnessSigla: artifactWitnesses.map(witness => typeof witness?.sigla === 'string' ? witness.sigla : ''),
          fallbackPrefix: 'Cx'
        }),
        labelNumber: Number.isFinite(row?.labelNumber) ? Number(row.labelNumber) : (index + 1),
        position: Number.isFinite(row?.position) ? Number(row.position) : (index + 1),
        unitMappings,
        createdAt: typeof row?.createdAt === 'string' ? row.createdAt : nowIso,
        updatedAt: nowIso,
        createdBy: typeof row?.createdBy === 'string' ? row.createdBy : 'workbench-build-import',
        status,
        metadata: normalizeAnchoredRowMetadata(row?.metadata)
      }
    })
    .filter((row): row is AnchoredRow => Boolean(row))

  if (existingRows.length > 0 && replaceExistingRows) {
    await replaceResolvedRows(contextId, existingRows, importedRows, { autoRelabel: false })
  } else if (importedRows.length > 0) {
    await upsertRows(contextId, importedRows, { autoRelabel: false })
  }

  return {
    rowCount: importedRows.length,
    witnessCount: importedWitnessIdsByArtifactId.size
  }
}

const normalizeUnitMappings = (row: AnchoredRow): AnchoredRow['unitMappings'] =>
  row.unitMappings.map(mapping => ({ ...mapping }))

const buildImportedRowIdForContext = (contextId: string, sourceRowId: unknown): string | null => {
  const normalized = typeof sourceRowId === 'string' ? sourceRowId.trim() : ''
  if (!normalized) {
    return null
  }
  return `build_import::${contextId}::${normalized}`
}

export const applyWorkbenchBuildArtifactWitnessReplay = async ({
  contextId,
  existingWitnesses,
  currentRows,
  artifact,
  upsertRows,
  updateWitnessProcessingResultsBatch
}: ApplyWorkbenchBuildArtifactWitnessReplayOptions): Promise<{
  witnessCount: number
  changedRowCount: number
  changedRowIds: string[]
  updatedRows: AnchoredRow[]
  changedRows: AnchoredRow[]
}> => {
  const payload = artifact.chapter_import_payload || {}
  const artifactWitnesses = Array.isArray(payload.witnesses) ? payload.witnesses : []
  const segmentationResults = payload.segmentation_results || {}
  const artifactRows = Array.isArray(payload.anchored_rows) ? payload.anchored_rows : []

  if (artifactWitnesses.length === 0 || artifactRows.length === 0) {
    throw new Error('Replay artifact did not contain any witness mappings.')
  }

  const currentWitnessBySigla = new Map(existingWitnesses.map(witness => [witness.sigla, witness]))
  const artifactWitnessIdToCurrentWitness = new Map<string, Witness>()

  artifactWitnesses.forEach(artifactWitness => {
    const sigla = typeof artifactWitness?.sigla === 'string' ? artifactWitness.sigla.trim() : ''
    const artifactWitnessId = typeof artifactWitness?.id === 'string' ? artifactWitness.id : ''
    const currentWitness = sigla ? currentWitnessBySigla.get(sigla) : undefined
    if (artifactWitnessId && currentWitness) {
      artifactWitnessIdToCurrentWitness.set(artifactWitnessId, currentWitness)
    }
  })

  if (artifactWitnessIdToCurrentWitness.size === 0) {
    throw new Error('None of the replay witnesses exist in the current context.')
  }

  const replayWitnessIds = new Set(
    Array.from(artifactWitnessIdToCurrentWitness.values()).map(witness => witness.id)
  )

  const segmentationUpdates = Array.from(artifactWitnessIdToCurrentWitness.entries())
    .map(([artifactWitnessId, witness]) => {
      const result = segmentationResults[artifactWitnessId]
      if (!result) {
        return null
      }
      return {
        witnessId: witness.id,
        type: 'segmentation' as const,
        result,
        status: 'completed' as const
      }
    })
    .filter((entry): entry is NonNullable<typeof entry> => Boolean(entry))

  const currentRowById = new Map(currentRows.map(row => [row.id, row]))
  const rowsAfterWitnessClear = currentRows.map(row => ({
    ...row,
    unitMappings: normalizeUnitMappings(row).filter(mapping => !replayWitnessIds.has(mapping.witnessId))
  }))
  const rowsAfterWitnessClearById = new Map(rowsAfterWitnessClear.map(row => [row.id, row]))

  const missingTargetRows: string[] = []
  artifactRows.forEach(artifactRow => {
    const rawMappings = Array.isArray(artifactRow?.unitMappings) ? artifactRow.unitMappings : []
    const replayMappings = rawMappings
      .map(mapping => {
        const artifactWitnessId = typeof mapping?.witnessId === 'string' ? mapping.witnessId : ''
        const currentWitness = artifactWitnessIdToCurrentWitness.get(artifactWitnessId)
        const unitUuid = typeof mapping?.unitUuid === 'string' ? mapping.unitUuid : ''
        if (!currentWitness || !unitUuid) {
          return null
        }
        return {
          witnessId: currentWitness.id,
          unitUuid,
          unitLabel: typeof mapping?.unitLabel === 'string' ? mapping.unitLabel : undefined,
          textSnapshot: typeof mapping?.textSnapshot === 'string' ? mapping.textSnapshot : undefined,
          confidence: typeof mapping?.confidence === 'number' ? mapping.confidence : undefined,
          translationStatus: mapping?.translationStatus,
          excluded: typeof mapping?.excluded === 'boolean' ? mapping.excluded : undefined,
          addedAt: typeof mapping?.addedAt === 'string' ? mapping.addedAt : new Date().toISOString()
        }
      })
      .filter((mapping): mapping is NonNullable<typeof mapping> => Boolean(mapping))

    if (replayMappings.length === 0) {
      return
    }

    const currentRowId = buildImportedRowIdForContext(contextId, artifactRow?.id)
    if (!currentRowId || !rowsAfterWitnessClearById.has(currentRowId)) {
      const targetLabel = typeof artifactRow?.id === 'string' && artifactRow.id.trim().length > 0
        ? artifactRow.id.trim()
        : '(missing row id)'
      missingTargetRows.push(targetLabel)
      return
    }

    const currentRow = rowsAfterWitnessClearById.get(currentRowId)!
    rowsAfterWitnessClearById.set(currentRowId, {
      ...currentRow,
      unitMappings: [...currentRow.unitMappings, ...replayMappings]
    })
  })

  if (missingTargetRows.length > 0) {
    throw new Error(
      `Replay artifact is incompatible with the current collation: ${missingTargetRows.length} row target${missingTargetRows.length === 1 ? '' : 's'} could not be matched: ${missingTargetRows.join(', ')}.`
    )
  }

  const changedRows = Array.from(rowsAfterWitnessClearById.values()).filter(row => {
    const previous = currentRowById.get(row.id)
    return JSON.stringify(previous?.unitMappings || []) !== JSON.stringify(row.unitMappings)
  })

  if (segmentationUpdates.length > 0) {
    await updateWitnessProcessingResultsBatch(segmentationUpdates)
  }
  if (changedRows.length > 0) {
    await upsertRows(contextId, changedRows, { autoRelabel: false })
  }

  return {
    witnessCount: artifactWitnessIdToCurrentWitness.size,
    changedRowCount: changedRows.length,
    changedRowIds: changedRows.map(row => row.id),
    updatedRows: Array.from(rowsAfterWitnessClearById.values()),
    changedRows
  }
}
